import React, { useState } from 'react';

const WORKER_COLORS = {
  origination: 'bg-blue-100 text-blue-700',
  regulatory_structuring: 'bg-primary-100 text-primary-700',
  technology: 'bg-cyan-100 text-cyan-700',
  distribution: 'bg-amber-100 text-amber-700',
};

const LinkIcon = () => (
  <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const DocIcon = () => (
  <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
  </svg>
);

/**
 * Resolves the best available link for a finding.
 * Priority: source_url > source (if URL) > generated Google search link
 */
const getSourceInfo = (finding) => {
  const isUrl = (s) => s && (s.startsWith('http://') || s.startsWith('https://'));

  // 1. Explicit source_url field
  if (isUrl(finding.source_url)) {
    return { url: finding.source_url, label: finding.source || 'Source', isSearch: finding.source_url.includes('google.com/search') };
  }

  // 2. Source field is itself a URL
  if (isUrl(finding.source)) {
    return { url: finding.source, label: finding.source, isSearch: false };
  }

  // 3. Generate a Google search URL from source name + title
  const searchTerms = [finding.source, finding.title].filter(Boolean).join(' ');
  const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(searchTerms)}`;
  return { url: googleUrl, label: finding.source || 'Search', isSearch: true };
};

const SourceDisplay = ({ finding }) => {
  const info = getSourceInfo(finding);

  return (
    <div className="flex items-center gap-2 mt-2">
      <DocIcon />
      <span className="text-xs text-gray-500 font-medium">{finding.source || 'Unknown source'}</span>
      <span className="text-gray-300">|</span>
      <a
        href={info.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-primary-600 hover:text-primary-800 hover:underline inline-flex items-center gap-1"
      >
        <LinkIcon />
        {info.isSearch ? 'Verify Source' : 'View Source'}
      </a>
    </div>
  );
};

const ExternalResearchCard = ({ research, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showRelevance, setShowRelevance] = useState({});

  if (!research) return null;

  const toggleRelevance = (idx) => {
    setShowRelevance(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const findingsCount = research.findings?.length || 0;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 bg-white">
      {/* Clickable Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900">{research.research_point}</h4>
            {findingsCount > 0 && (
              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                {findingsCount} finding{findingsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {research.summary && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{research.summary}</p>
          )}
          <div className="flex gap-2 mt-2 flex-wrap">
            {(research.relevance_to_workers || []).map(worker => (
              <span
                key={worker}
                className={`px-2 py-0.5 rounded text-xs font-medium ${WORKER_COLORS[worker] || 'bg-gray-100 text-gray-600'}`}
              >
                {worker.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-3 ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
          {/* Triggered By */}
          {research.triggered_by && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Triggered By</h5>
              <p className="text-sm text-gray-600">
                {research.triggered_by.question}: {research.triggered_by.response}
                {research.triggered_by.question2 && (
                  <> + {research.triggered_by.question2}: {research.triggered_by.response2}</>
                )}
              </p>
            </div>
          )}

          {/* Findings */}
          {research.findings && research.findings.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Findings</h5>
              <div className="space-y-3">
                {research.findings.map((finding, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{finding.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{finding.key_data}</p>

                    {/* Source with link */}
                    <SourceDisplay finding={finding} />

                    {/* Relevance toggle */}
                    {finding.relevance && (
                      <div className="mt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleRelevance(idx); }}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          {showRelevance[idx] ? 'Hide relevance' : 'Show relevance'}
                        </button>
                        {showRelevance[idx] && (
                          <p className="text-xs text-gray-500 mt-1 pt-1 border-t border-gray-100 italic">
                            {finding.relevance}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExternalResearchCard;
